import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  Clock, 
  ChevronRight,
  Scissors,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import ServiceProviderBadges from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ServiceCardDual (Grid Card C)
 * Dual-action card with both fixed price and quote options
 * Used for: Services that offer both simple fixes and custom work
 */
const ServiceCardDual = ({ 
  service, 
  onBookFixed, 
  onRequestQuote,
  onViewDetails,
  className = '' 
}) => {
  const {
    id,
    provider_name,
    provider_image,
    service_type,
    service_name,
    rating = 0,
    review_count = 0,
    fixed_price_services = [],
    quote_services = [],
    currency = 'Rs',
    distance,
    response_time,
    verification_level = 0,
    achievements = [],
    is_sponsored = false,
  } = service;

  const imageUrl = provider_image 
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

  // Get the first fixed price service for display
  const featuredFixed = fixed_price_services[0];
  const hasQuoteServices = quote_services.length > 0;

  return (
    <Card 
      className={`group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-2 hover:border-purple-400 cursor-pointer ${className}`}
      onClick={() => onViewDetails?.(service)}
      data-testid={`service-card-dual-${id}`}
    >
      {/* Sponsored Tag */}
      {is_sponsored && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-3 py-1 flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          <span>Featured Provider</span>
        </div>
      )}
      
      <CardContent className="p-4">
        {/* Provider Info */}
        <div className="flex items-start gap-3 mb-4">
          <img
            src={imageUrl}
            alt={provider_name}
            className="w-14 h-14 rounded-full object-cover border-2 border-purple-200"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';
            }}
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate group-hover:text-purple-600 transition-colors">
              {provider_name}
            </h3>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                <span className="font-semibold text-sm">{rating.toFixed(1)}</span>
              </div>
              <span className="text-xs text-gray-500">({review_count})</span>
            </div>
            <ServiceProviderBadges 
              verificationLevel={verification_level}
              achievements={achievements}
              maxBadges={2}
            />
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4 pb-4 border-b">
          {distance && (
            <div className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              <span>{distance}</span>
            </div>
          )}
          {response_time && (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>{response_time}</span>
            </div>
          )}
        </div>
        
        {/* Dual Action Options */}
        <div className="space-y-3">
          {/* Fixed Price Option */}
          {featuredFixed && (
            <div 
              className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:border-green-400 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onBookFixed?.(service, featuredFixed);
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-500 rounded-full">
                    <Scissors className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{featuredFixed.name}</p>
                    <p className="text-xs text-gray-500">Fixed price • Book instantly</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">
                    {currency} {featuredFixed.price?.toLocaleString()}
                  </p>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    className="text-green-600 hover:text-green-700 p-0 h-auto"
                    data-testid={`book-fixed-${id}`}
                  >
                    Book <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </div>
              {fixed_price_services.length > 1 && (
                <p className="text-xs text-green-600 mt-2">
                  +{fixed_price_services.length - 1} more fixed-price services
                </p>
              )}
            </div>
          )}
          
          {/* Quote Option */}
          {hasQuoteServices && (
            <div 
              className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 hover:border-purple-400 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onRequestQuote?.(service);
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-500 rounded-full">
                    <Sparkles className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Custom Work</p>
                    <p className="text-xs text-gray-500">
                      {quote_services.slice(0, 2).join(', ')}
                      {quote_services.length > 2 && ` +${quote_services.length - 2}`}
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  data-testid={`request-quote-dual-${id}`}
                >
                  Get Quote
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceCardDual;
